import Link from 'next/link';
import { Button, Col, Row } from 'react-bootstrap';
import Confetti from 'react-confetti';

import { useMemo } from 'react';
import { SuccessBookingPageQuery } from '../../types/graphqlOperations';

import SeoMetaTags from '../seoMetaTags';
import UserPhoneNumberUpdate from './utils/UserPhoneNumberUpdate';

/**
 * Success
 *
 * Shows as success message after the booking has been made, and submits tracking data
 */
export function Success({
  booking,
}: {
  booking: SuccessBookingPageQuery['booking'];
}) {
  const linkToTripForm = useMemo(() => {
    if (booking.spaces.filter((space) => space.active).length > 1) {
      return `/me/${booking.user.id}/bookings/${booking.id}/guests`;
    }
    const spaceId = booking.spaces.filter((s) => s.active)[0]?.id;
    return `/me/${booking.user.id}/guestForm/${spaceId}`;
  }, [booking.id, booking.spaces, booking.user.id]);

  return (
    <>
      <SeoMetaTags description="You're booked on" title="You're booked on!" />

      <Confetti
        initialVelocityX={8}
        initialVelocityY={8}
        numberOfPieces={400}
        gravity={0.2}
        recycle={false}
      />

      <Row>
        <Col xs={12} sm={8} md={7} className="mx-auto">
          <h1>You're booked on!</h1>

          <Col className="py-2">
            <p>
              Thanks for booking through Skyhook! We've just sent you an email
              with all your trip details.
            </p>
            <p>
              Next, please complete the steps below which relate to your
              booking.
            </p>
            <p className="fw-bold">
              1. Add your phone number in case of emergency
            </p>
            <p>
              <UserPhoneNumberUpdate user={booking.user} />
            </p>
            <p className="fw-bold">2. Complete your trip form</p>
            <p>
              <div className="d-grid gap-2">
                <Link href={linkToTripForm} legacyBehavior passHref>
                  <Button as="a" variant="primary" target="_blank">
                    Complete Trip Form
                  </Button>
                </Link>
              </div>
            </p>
            <p className="fw-bold">3. Get travel insurance</p>
            <p>
              Get travel insurance that will cover you for adventure activities.
              We recommend the adventure travel insurance specialist,{' '}
              <a
                href="https://riseandshield.com/"
                target="_blank"
                rel="noreferrer"
              >
                Rise & Shield
              </a>{' '}
              - they cover 100s of activities and are easy to deal with if you
              need to make a claim.
            </p>
            <div className="d-grid gap-2 my-5">
              <Link
                href={`/me/${booking.user.id}/bookings/${booking.id}`}
                legacyBehavior
                passHref
              >
                <Button as="a" variant="primary" target="_blank">
                  View Booking
                </Button>
              </Link>
            </div>
          </Col>
        </Col>
      </Row>
    </>
  );
}
